import { classNames } from "@/lib/utils";
import { File } from "@/types/app/file";
import { RadixAppDialog } from "@components/library/AppDialog";
import Button from "@components/library/Button";
import { ButtonWithShortcut } from "@components/atoms/ButtonWithShortcut";
import { VersionPopover } from "@components/library/Entities/VersionPopover";
import { hlToastApiError, ToastVariant, hlToast } from "@components/library/Toast";
import { FormikTextarea, FormikTextInput } from "@components/molecules/FormikInput";
import { CheckCircle } from "@phosphor-icons/react";
import { useActiveOrganization } from "@/context/ActiveOrganizationProvider";
import axios from "axios";

import { Form, Formik } from "formik";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import { getShortId } from "@/lib/string";
import { CommandIcon, ReturnIcon } from "@components/library/Icons";
import { getNextVersionName, normalizeVersionFields } from "@components/library/utils/versions";

export type FileVersionSaveHandler = (
  versionName: string | null,
  versionDescription: string | null,
) => Promise<File | undefined>;

export interface SaveVersionDialogProps {
  open: boolean;
  onClose: () => void;
  /* The function to handle saving the version (the upsertion operation) */
  handleSave: FileVersionSaveHandler;
  /* Callback after saving (for refetching etc.) */
  onSave?: (version: File) => void;
  /**
   * Whether to show the outcome step after creating a version.
   *
   * If false, the modal will close after creating a version.
   */
  showOutcome?: boolean;
  /* Show a button also then handle deployment */
  handleDeployStep?: () => void;
  /**
   * Pre-defined version name to use (takes precedence over generated names)
   */
  initialVersionName?: string;
  /**
   * Current version name used to generate the next suggested version name
   * (e.g., "My File v1" → "My File v2")
   */
  currentVersionName?: string | null;
  /**
   * Pre-defined description for the version. For now, not passed in anywhere.
   * In future, we could consider inferring the description from the changes made to the version.
   */
  initialDescription?: string;
}

export const SaveVersionDialog = ({
  open,
  showOutcome = true,
  onClose,
  handleSave,
  onSave,
  initialVersionName,
  currentVersionName,
  initialDescription,
  handleDeployStep,
}: SaveVersionDialogProps) => {
  const [step, setStep] = useState<"create" | "outcome">("create");
  const [createdVersion, setCreatedVersion] = useState<File>();

  const suggestedVersionName = useMemo(() => {
    if (initialVersionName) return initialVersionName;
    return getNextVersionName(currentVersionName);
  }, [initialVersionName, currentVersionName]);

  const handleClose = async () => {
    setStep("create");
    onClose();
  };

  const showSuccessToast = (version: File) => {
    const fileType = version.type.charAt(0).toUpperCase() + version.type.slice(1);
    const versionName = version.version_name;

    hlToast(
      {
        title: versionName
          ? `Saved ${fileType} version '${versionName}'`
          : `Saved ${fileType} version with id #${getShortId(version.version_id)}`,
        description: (
          <div className="flex flex-col gap-8">
            <div>{version.version_description || "No description provided"}</div>
          </div>
        ),
        variant: ToastVariant.Success,
      },
      { duration: 7000 },
    );
  };

  return (
    <>
      {step == "create" && (
        <RadixAppDialog title="Save Version" onClose={handleClose} open={open} width="sm">
          <SaveVersionStep
            suggestedVersionName={suggestedVersionName}
            suggestedVersionDescription={initialDescription}
            showOutcome={showOutcome}
            setStep={setStep}
            handleSave={handleSave}
            onSave={(version) => {
              showSuccessToast(version);
              onSave?.(version);
            }}
            onClose={handleClose}
            setCreatedVersion={setCreatedVersion}
          />
        </RadixAppDialog>
      )}

      {step == "outcome" && createdVersion && (
        <RadixAppDialog open={open} onClose={handleClose} title={undefined} className="flex">
          <SaveOutcomeStep createdVersion={createdVersion} onClose={handleClose} handleDeployStep={handleDeployStep} />
        </RadixAppDialog>
      )}
    </>
  );
};

const SaveOutcomeStep = ({
  onClose,
  createdVersion,
  handleDeployStep,
}: {
  onClose: () => void;
  createdVersion: File;
  handleDeployStep?: () => void;
}) => {
  return (
    <div className="-my-40 flex flex-col items-center justify-center gap-40 py-40">
      <div className="flex w-full  items-center justify-center gap-8 text-18-24 font-bold text-oldgray-900">
        <CheckCircle weight="bold" className="h-24 w-24 text-icon-base-1" />
        Saved
      </div>

      <VersionPopover file={createdVersion} onFileChange={() => {}} isEditable={false} />
      <div className="flex w-full justify-between  gap-12">
        <Button
          styling="outline"
          size={36}
          onClick={() => {
            onClose();
          }}
        >
          Close
        </Button>
        {handleDeployStep ? (
          <Button
            IconLeft={(props: any) => (
              <CheckCircle {...props} weight="fill" className={"!text-icon-success-2 " + props.className} />
            )}
            styling="solid"
            shade="black"
            size={36}
            onClick={() => {
              onClose();
              handleDeployStep();
            }}
          >
            Deploy...
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const SaveVersionStep = ({
  suggestedVersionName,
  suggestedVersionDescription,
  showOutcome,
  setStep,
  handleSave,
  onSave,
  onClose,
  setCreatedVersion,
}: {
  suggestedVersionName?: string;
  suggestedVersionDescription?: string;
  showOutcome: boolean;
  setStep: (step: "outcome") => void;
  handleSave: FileVersionSaveHandler;
  onSave?: (version: File) => void;
  onClose: () => void;
  setCreatedVersion: (version: File) => void;
}) => {
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <Formik
        initialValues={{ name: suggestedVersionName || "", description: suggestedVersionDescription || "" }}
        validateOnBlur={false}
        onSubmit={async (values, { setErrors, setSubmitting }) => {
          if (creating) return;
          setCreating(true);
          try {
            // If values are empty, coerce to null, as these fields are nullable in the API
            const versionNameNormalized = normalizeVersionFields.name(values.name);
            const versionDescriptionNormalized = normalizeVersionFields.description(values.description);
            const versionResponse = await handleSave(versionNameNormalized, versionDescriptionNormalized);
            if (!versionResponse) {
              throw new Error("Failed to create version");
            }
            setCreatedVersion(versionResponse);
            if (showOutcome) {
              setStep("outcome");
            } else {
              onClose();
            }
            onSave && onSave(versionResponse);
          } catch (e: unknown) {
            if (axios.isAxiosError(e)) {
              if (e.response?.status === 409) {
                // Use Formik's setErrors instead of custom state
                setErrors({
                  name: "This version name already exists. Please choose a different name.",
                });
                // Don't close the modal, allow the user to fix the error
                setCreating(false);
                return;
              }
              hlToastApiError({
                error: e,
                titleFallback: "Failed to create version",
              });
              onClose();
            } else {
              console.error("Unexpected error:", e);
              onClose();
            }
          } finally {
            setCreating(false);
          }
        }}
        validationSchema={Yup.object({
          name: Yup.string(),
          description: Yup.string(),
        })}
      >
        {({ isSubmitting, submitForm, setErrors }) => {
          const handleCmdEnter = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              submitForm();
            }
          };

          return (
            // @ts-ignore
            <Form className="flex flex-col gap-24">
              <div className="flex flex-col gap-20">
                <div className="flex flex-col gap-4">
                  <FormikTextInput
                    name="name"
                    label={
                      <div>
                        Version name <span className="text-text-base-3">(optional)</span>
                      </div>
                    }
                    autoFocus
                    disabled={isSubmitting}
                    tabIndex={1}
                    onKeyDown={handleCmdEnter}
                  />
                </div>

                <div className="flex flex-col gap-4">
                  <FormikTextarea
                    name="description"
                    label={
                      <div>
                        Description <span className="text-text-base-3">(optional)</span>
                      </div>
                    }
                    rows={3}
                    tabIndex={2}
                    placeholder={"Describe the changes you made"}
                    className={classNames(isSubmitting ? "bg-oldgray-200" : "", "max-h-[400px]")}
                    disabled={isSubmitting}
                    onKeyDown={handleCmdEnter}
                  />
                </div>
              </div>
              <div className="flex justify-between gap-8 ">
                <Button styling="outline" size={36} onClick={onClose} tabIndex={3} disabled={isSubmitting}>
                  Continue Editing
                </Button>
                <ButtonWithShortcut
                  styling="solid"
                  shade="black"
                  size={36}
                  type="submit"
                  onClick={submitForm}
                  tabIndex={4}
                  disabled={isSubmitting}
                  shortcut="cmd+enter"
                  hideShortcut
                >
                  <span className="mr-3">Save</span>
                  <div className="flex items-center gap-6">
                    <CommandIcon className="-my-1 h-[9px] w-[9px]" strokeWidth={2.5} />
                    <ReturnIcon className="-my-1 h-[9px] w-[9px]" strokeWidth={2.5} />
                  </div>
                </ButtonWithShortcut>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
